/**
 * Single transaction row component
 */
import { memo } from 'react';
import { format, parseISO } from 'date-fns';
import type { TransactionWithSplits } from '@/services/transactions';
import { formatCurrency } from '@/utils/format';
import { useCategoryById } from '@/hooks';
import { cn } from '@/utils/cn';

export interface TransactionRowProps {
  transaction: TransactionWithSplits;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  onClick?: (transaction: TransactionWithSplits) => void;
  showCheckbox?: boolean;
}

/**
 * Generate merchant avatar - first letter or icon
 */
function MerchantAvatar({ name, className }: { name: string; className?: string }) {
  const initial = (name || '?').charAt(0).toUpperCase();

  // Generate a consistent color based on the name
  const colors = [
    'bg-blue-100 text-blue-600',
    'bg-green-100 text-green-600',
    'bg-yellow-100 text-yellow-600',
    'bg-purple-100 text-purple-600',
    'bg-pink-100 text-pink-600',
    'bg-indigo-100 text-indigo-600',
    'bg-red-100 text-red-600',
    'bg-orange-100 text-orange-600',
  ];

  const colorIndex = name
    ? name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length
    : 0;

  return (
    <div
      className={cn(
        'flex items-center justify-center w-10 h-10 rounded-full font-semibold text-sm',
        colors[colorIndex],
        className
      )}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

/**
 * Transaction row for list display
 */
function TransactionRow({
  transaction,
  isSelected = false,
  onSelect,
  onClick,
  showCheckbox = false,
}: TransactionRowProps) {
  // Use category from transaction if available (from API), otherwise fall back to lookup
  const lookedUpCategory = useCategoryById(
    !transaction.categoryName ? transaction.categoryId : undefined
  );
  const categoryName = transaction.categoryName || lookedUpCategory?.name;

  const displayName = transaction.merchantName || transaction.name || 'Unknown';

  // Safely format date with fallback
  let formattedDate = 'Unknown date';
  try {
    if (transaction.date) {
      formattedDate = format(parseISO(transaction.date), 'MMM d, yyyy');
    }
  } catch {
    // Keep default 'Unknown date' if parsing fails
  }

  const isIncome = transaction.type === 'income';
  // WP-TRANSFER-5: Use the isTransfer boolean flag, not type field
  const isTransfer = transaction.isTransfer;

  const handleClick = () => {
    if (onClick) {
      onClick(transaction);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(transaction.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={cn(
        'flex items-center px-4 py-3 hover:bg-gray-50 border-b border-gray-100 cursor-pointer transition-colors',
        isSelected && 'bg-primary-50 hover:bg-primary-50'
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Transaction: ${displayName}, ${formatCurrency(Math.abs(transaction.amount), transaction.currency)}`}
    >
      {/* Checkbox for bulk selection */}
      {showCheckbox && (
        <div className="mr-3" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            aria-label={`Select ${displayName}`}
          />
        </div>
      )}

      {/* Merchant avatar */}
      <MerchantAvatar name={displayName} />

      {/* Transaction details */}
      <div className="ml-3 flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900 truncate">
            {displayName}
          </p>
          <p
            className={cn(
              'text-sm font-semibold whitespace-nowrap ml-2',
              isIncome ? 'text-green-600' : isTransfer ? 'text-blue-600' : 'text-gray-900'
            )}
          >
            {isIncome ? '+' : isTransfer ? '' : '-'}
            {formatCurrency(Math.abs(transaction.amount), transaction.currency)}
          </p>
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <div className="flex items-center text-sm text-gray-500 truncate">
            <span>{formattedDate}</span>
            <>
              <span className="mx-1.5" aria-hidden="true">
                &bull;
              </span>
              <span className={cn(
                "truncate",
                !categoryName && "text-gray-400 italic"
              )}>
                {categoryName || 'Uncategorized'}
              </span>
            </>
          </div>
          {transaction.pending && (
            <span className="flex-shrink-0 ml-2 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
              Pending
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(TransactionRow);
